import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bell, CircleDashed, SignIn, SignOut, Ticket, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICON = {
  agent_online: SignIn,
  agent_offline: SignOut,
  ticket_assigned: Ticket,
  ticket_transferred: ArrowsLeftRight,
};

const TONE = {
  agent_online: "text-[#16A34A]",
  agent_offline: "text-gray-500",
  ticket_assigned: "text-[#0047AB]",
  ticket_transferred: "text-orange-500",
};

const formatRel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

// Web-Audio "ding" — 2 short tones. No external file needed.
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const play = (freq, start, dur, gain = 0.15) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(gain, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.02);
    };
    play(880, 0, 0.18);
    play(1320, 0.16, 0.22);
    setTimeout(() => ctx.close(), 700);
  } catch { /* silent */ }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const lastIdsRef = useRef(new Set());
  const initialisedRef = useRef(false);
  const soundEnabledRef = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      // Detect new items (ids we haven't seen yet)
      const knownIds = lastIdsRef.current;
      const currentIds = new Set(data.map((n) => n.id));
      if (initialisedRef.current) {
        const fresh = data.filter((n) => !knownIds.has(n.id));
        // Only alert on ticket-assignment notifications relevant to me
        const mine = fresh.filter(
          (n) => (n.kind === "ticket_assigned" || n.kind === "ticket_transferred") &&
                 (n.for_user_id === user?.id || (!n.for_user_id && user?.role === "admin"))
        );
        if (mine.length > 0) {
          if (soundEnabledRef.current) playDing();
          mine.slice(0, 3).forEach((n) => {
            toast(n.message, {
              description: n.ticket_number ? `Ticket ${n.ticket_number}` : undefined,
              action: n.ticket_id
                ? { label: "Open", onClick: () => navigate(`/tickets/${n.ticket_id}`) }
                : undefined,
              duration: 8000,
            });
          });
        }
      }
      lastIdsRef.current = currentIds;
      initialisedRef.current = true;

      setItems(data);
      const u = data.filter((n) => !(n.read_by || []).includes(user?.id)).length;
      setUnread(u);
    } catch { /* forbidden → ignored */ }
  };

  useEffect(() => {
    if (!user) return;
    // Unlock audio on first click anywhere (browsers block auto-play)
    const unlock = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) new Ctx().close();
      } catch { /* ignore */ }
      document.removeEventListener("click", unlock);
    };
    document.addEventListener("click", unlock);

    load();
    const t = setInterval(load, 10_000);
    return () => { clearInterval(t); document.removeEventListener("click", unlock); };
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/mark-read"); } catch { /* */ }
      setUnread(0);
    }
  };

  const clickItem = async (n) => {
    if (n.ticket_id) {
      setOpen(false);
      navigate(`/tickets/${n.ticket_id}`);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        data-testid="notification-bell"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-sm border border-gray-200 hover:bg-gray-50"
        title="Notifications"
      >
        <Bell size={16} weight="bold" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full bg-[#FF2400] text-white flex items-center justify-center animate-pulse">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[28rem] overflow-y-auto bg-white border border-gray-200 rounded-sm shadow-lg z-50" data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-gray-200 sticky top-0 bg-white flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider font-bold">Notifications</p>
            <button
              onClick={() => { soundEnabledRef.current = !soundEnabledRef.current; setOpen(open); toast.message(soundEnabledRef.current ? "Sound on" : "Sound muted"); }}
              className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-800"
              data-testid="notification-sound-toggle"
              title="Toggle notification sound"
            >
              🔔 sound
            </button>
          </div>
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-gray-500 flex flex-col items-center gap-2">
              <CircleDashed size={20} weight="bold" />
              No notifications yet
            </div>
          )}
          <ul className="divide-y divide-gray-100" data-testid="notification-list">
            {items.map((n) => {
              const Icon = ICON[n.kind] || Bell;
              const tone = TONE[n.kind] || "text-gray-500";
              const clickable = !!n.ticket_id;
              return (
                <li
                  key={n.id}
                  onClick={() => clickItem(n)}
                  className={`px-4 py-3 flex gap-2 ${clickable ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <Icon size={16} weight="bold" className={`${tone} mt-0.5 shrink-0`} />
                  <div className="flex-1">
                    <p className="text-xs font-medium">{n.message}</p>
                    {n.tickets_reassigned > 0 && (
                      <p className="text-[10px] text-gray-500 mt-0.5">{n.tickets_reassigned} ticket(s) reassigned</p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-0.5">{formatRel(n.created_at)}</p>
                  </div>
                  {clickable && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#0047AB] self-center">Open →</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
